import { h, Fragment } from "preact";
import { useState, useEffect, useRef } from "preact/hooks";
import moment from "moment";
import {
  Modal,
  Typography,
  Button,
  Select,
  Input,
  Skeleton,
  Spin,
  Image,
  Tooltip,
  Dropdown,
  Menu,
} from "antd";
import axios from "axios";
import {
  FormOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  CopyOutlined,
  EllipsisOutlined,
  DownloadOutlined,
  DownOutlined,
  UndoOutlined,
  FullscreenOutlined,
  RedoOutlined,
  PlayCircleOutlined,
  LoadingOutlined,
  LeftOutlined,
  RightOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import {
  getDefaultStateParams,
  convertToCountryName,
  calculateDurationInSeconds,
  capitalizeFirstLetter,
  convertDateFormat,
  createDownloadHandlers,
  buildServiceArtifactMap,
  extractFlowFormIdentity,
  ERROR_CODE_TRANSLATIONS,
  ERROR_MESSAGE_CODES,
  KISC_ERROR_TRANSLATIONS,
} from "./TransactionsUtils";
import VendorDisplay from "../../utils/VendorDisplay";
const { Option } = Select;
import TransactionHistoryPage from "./TransactionHistoryPage";

const SERVICE_STATUS_NAME_MAP = {
  "egov-doc": "Document Recognition",
  "veridoc": "Document Recognition",
  "redoc": "Document Recognition",
  "verilive": "Liveness Detection",
  "veriface": "Face Matching (1:1)",
  "amlcft-service": "AML / CFT Check",
  "veriphone": "Phone Verification",
  "kyc-list": "Blacklist",
  "erd": "ERD (Register of Debtors)",
  "consent": "Consent",
  "kisc": "KISC",
  "doc-preview": "Document Preview",
  "flow-form": "Flow Form"
};

const getProjectionServiceKey = (projection) => {
  if (projection.service_name === "esign") {
    return `Esign - ${projection.result?.action || ""}`;
  }
  return SERVICE_STATUS_NAME_MAP[projection.service_name] || projection.service_name;
};

const buildServiceAttempts = (transactionInfo) => {
  const attempts = {};
  (transactionInfo?.session_projections || []).forEach((projection) => {
    const key = getProjectionServiceKey(projection);
    if (!attempts[key]) attempts[key] = [];
    attempts[key].push(projection);
  });
  Object.values(attempts).forEach((list) =>
    list.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
  );
  return attempts;
};

const SIDEBAR_WIDTH = 250;
const MODAL_LEFT_OFFSET = 120;
const MODAL_TOP_OFFSET = 30;
const MODAL_MIN_WIDTH = "1000px";
const MODAL_MAX_WIDTH = "1440px";

const TransactionModalPage = ({
  visible,
  onCancel,
  flowId,
  transactionInfo,
  canManualEdit,
  cacheState,
  updateCache,
}) => {
  const { statusOptions, defaultDocData, serviceStatuses } =
    getDefaultStateParams();

  const defaultInputData = { iin: "-", phone_number: "-" };
  const [editing, setEditing] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(
    transactionInfo?.flow_status
  );
  const [manualType, setManualType] = useState(false);
  const [inputData, setInputData] = useState(defaultInputData);
  const [isDocEditing, setIsDocEditing] = useState(false);
  const [faceImg, setFaceImg] = useState(null);
  const [frontImg, setFrontImg] = useState(null);
  const [backImg, setBackImg] = useState(null);
  const [largeImg, setLargeImg] = useState(null);
  const [liveVideo, setLiveVideo] = useState(null);
  const [livenessVideo, setLivenessVideo] = useState(null);
  const [livenessCard, setLivenessCard] = useState(null);
  const [livenessFrames, setLivenessFrames] = useState(null);
  const [clientCard, setClientCard] = useState(null);
  const [largeFrontImg, setLargeFrontImg] = useState(null);
  const [largeBackImg, setLargeBackImg] = useState(null);
  const [bestframeLarge, setBestframeLarge] = useState(null);
  const [bestframeSmall, setBestframeSmall] = useState(null);
  const [signedDocs, setSignedDocs] = useState(null);
  const [egovDocPdf, setEgovDocPdf] = useState(null);

  const [showSpinner, setShowSpinner] = useState(true);
  const [showNoImage, setShowNoImage] = useState(false);
  const [showLiveVideo, setShowLiveVideo] = useState(false);

  // const [mappedServices, setMappedServices] = useState(serviceStatuses);
  const [mappedServices, setMappedServices] = useState({});

  const [serviceAttempts, setServiceAttempts] = useState({});
  const [selectedAttempts, setSelectedAttempts] = useState({});
  const [attemptArtifactCache, setAttemptArtifactCache] = useState({});
  const [attemptLoading, setAttemptLoading] = useState({});
  const attemptFetchSeq = useRef({});
  // Mirrors `selectedAttempts` so rebuilds triggered from an effect closure read
  // the current selection instead of the one captured when the effect ran.
  const selectedAttemptsRef = useRef({});

  const [historyOpen, setHistoryOpen] = useState(false);
  const [transactionForHistory, setTransactionForHistory] = useState(null);
  const [flowPipelineCache, setFlowPipelineCache] = useState({});
  const [currentPage, setCurrentPage] = useState("main");

  const [documentBody, setDocumentBody] = useState({});
  const extractServiceStatuses = (pipelineData) => {
    const newServiceStatuses = {};

    const isEquivalentService = (serviceName1, serviceName2) => {
      const equivalentGroups = {
        "veridoc": ["egov-doc", "redoc"],
        "egov-doc": ["veridoc"],
        "redoc": ["veridoc"]
      };
      return serviceName1 === serviceName2 || equivalentGroups[serviceName1]?.includes(serviceName2);
    };

    const getVendorForSession = (sessionId) => {
      const matchingProjection = transactionInfo?.session_projections?.find(
        projection => projection.session_id === sessionId
      );
      return matchingProjection?.vendor || null;
    };

    if (pipelineData === null) {
      transactionInfo?.service_statuses.forEach(service => {
        const vendor = getVendorForSession(service.session_id);
        processService(service, newServiceStatuses, service, vendor);
      });
    } else {
      (pipelineData ?? []).forEach(service => {
        const matchingService = transactionInfo?.service_statuses.find(status =>
            isEquivalentService(status.service_name, service.service_name) &&
            (service.service_name !== "esign" || service.action === status.session_result?.action)
        );
        const vendor = matchingService ? getVendorForSession(matchingService.session_id) : null;
        processService(service, newServiceStatuses, matchingService, vendor);
      });
    }

    return newServiceStatuses;
  };

  const renderTooltip = (tooltip) => {
    if (!tooltip) {
        return null;
    }
    if (Array.isArray(tooltip)) {
      return (
        <div>
          {tooltip.map((line, i) => <div key={i}>{i > 0 ? `- ${line}` : line}</div>)}
        </div>
      );
    }
    return tooltip;
  };

  const getErrorInfo = (existingStatus) => {
    if (existingStatus?.session_status === "pass") return { msg: "", tooltip: null };

    const codes = [
      existingStatus?.session_result?.error_code,
      ...(existingStatus?.session_result?.error_codes || [])
    ].filter(Boolean);

    if (!codes.length && existingStatus?.service_name === "veriface") {
      const serviceStatus = existingStatus?.session_result?.service_status;
      if (serviceStatus) codes.push(serviceStatus);
    }

    if (codes.length) {
      const errorMsg = existingStatus?.session_result?.error_msg;
      const unique = [...new Set(codes)];

      const translated = [];
      let tooltip = null;
      unique.forEach((c) => {
        if (ERROR_MESSAGE_CODES.has(c)) {
          // Parse the KISC vendor error from error_msg. The regex grabs the JSON object
          // that trails "response:" and reads its top-level "error" field (uppercased by
          // the server, hence .toLowerCase()).
          //
          // Real error_msg sample (additionalParams blob truncated for brevity):
          //   Could not verify against Kisc,  HTTP status code: 400, response: {"error":"IIN_NOT_FOUND","additionalParams":{"responseData":"{...}"},"requestId":"bbad8f66-..."}
          //
          // If the server changes this format the regex/JSON.parse may stop matching;
          // unknown or free-text errors (e.g. "Error obtaining KDP token") fall back to
          // "Сервис ЦОИД недоступен".
          let kiscMsg = "Сервис ЦОИД недоступен";
          if (errorMsg) {
            try {
              const match = errorMsg.match(/response:\s*(\{.+\})$/s);
              if (match) {
                const parsed = JSON.parse(match[1]);
                const key = parsed.error?.toLowerCase();
                const entry = key && KISC_ERROR_TRANSLATIONS[key];
                if (entry) {
                  kiscMsg = entry.msg;
                  if (!tooltip && entry.tooltip) tooltip = entry.tooltip;
                }
              }
            } catch (error) {
              console.error("Failed to parse KISC error_msg payload:", error);
            }
          }
          translated.push(kiscMsg);
        } else if (ERROR_CODE_TRANSLATIONS[c]) {
          translated.push(ERROR_CODE_TRANSLATIONS[c].msg);
          if (!tooltip && ERROR_CODE_TRANSLATIONS[c].tooltip) {
            tooltip = ERROR_CODE_TRANSLATIONS[c].tooltip;
          }
        }
      });

      return { msg: [...new Set(translated)].join(", "), tooltip };
    }

    return { msg: "", tooltip: null };
  };

  const processService = (service, newServiceStatuses, existingStatus = null, vendor) => {
    const capitalizeFirstLetter = (str) => str?.charAt(0).toUpperCase() + str?.slice(1) || "unknown";
    const statusMap = SERVICE_STATUS_NAME_MAP;

    let statusName =
        statusMap[service.service_name] ||
        (service.service_name === "esign" ?
            `Esign - ${service.action || (service.session_projections && service.session_projections.action) || ''}` :
            service.service_name);
    newServiceStatuses[statusName] = {
      status: existingStatus?.session_status || "Unknown",
      color: existingStatus ? determineColor(existingStatus) : "gray",
      result: existingStatus?.session_result
          ? (service.service_name === "veriface"
              ? existingStatus.session_result.score + "%"
              : capitalizeFirstLetter(existingStatus.session_result.service_status.replace(/_/g, " ")))
          : existingStatus?.sessions_count ? "Unknown" : "Not Started",
      counter: existingStatus?.sessions_count || 0,
      error: getErrorInfo(existingStatus),
    };

    if(vendor){
      newServiceStatuses[statusName] = {
      ...newServiceStatuses[statusName],
      vendor: vendor
      };
    }

    if ((service.service_name === "egov-doc" || service.service_name === "veridoc" || service.service_name === "redoc")) {
      setDocumentBody((prevDocumentBody) => ({
        ...prevDocumentBody,
        session_id: existingStatus?.session_id || "unknown",
        updated_body: {
          ...prevDocumentBody.updated_body,
          service_name: service.service_name,
          service_status: existingStatus?.session_result?.service_status || "unknown",
        },
      }));
    }
  };

  const determineColor = (service) => {
    if (service.service_name === "amlcft-service" || service.service_name === "kyc-list" || service.service_name === "erd") {
      return service.session_result?.service_status === "negative" ? "green" : "red";
    }
    return service.session_status === "pass"
        ? "green"
        : service.session_status === "fail"
            ? "red"
            : "yellow";
  };


  const extractDocumentData = () => {
    const documentData = {};

    transactionInfo?.session_projections?.forEach((projection) => {
      if (
        (projection.service_name === "egov-doc" ||
          projection.service_name === "veridoc" ||
          projection.service_name === "redoc") &&
        projection.result &&
        projection.result.document
      ) {
        const document = projection.result.document;
        documentData["First Name"] = [
          document.first_name || document.mrz_first_name || "-",
          "first_name",
        ];
        documentData["Last Name"] = [document.last_name || document.mrz_last_name || "-", "last_name"];
        documentData["Gender"] = [document.gender || document.mrz_gender || "-", "gender"];
        documentData["Date of birth"] = [
          document.birth_date || document.mrz_birth_date || "-",
          "birth_date",
        ];
        documentData["Country of Document"] = [
          document.birth_place || document.mrz_country || "-",
          "birth_place",
        ];
        documentData["Document type"] = [
          document.doc_type || "-",
          "doc_type",
        ];
        documentData["Document number"] = [
          document.id_number || document.mrz_doc_number || "-",
          "id_number",
        ];
        documentData["Issued by"] = [document.issuer || "-", "issuer"];
        documentData["Issue date"] = [
          document.issue_date || "-",
          "issue_date",
        ];
        documentData["Expiration date"] = [
          document.due_date || document.mrz_due_date || "-",
          "due_date",
        ];
      }
    });

    return documentData;
  };

  const [editedDocData, setEditedDocData] = useState(extractDocumentData());

  const [rotateDegree, setRotateDegree] = useState(0);
  const [visibleLarge, setVisibleLarge] = useState(false);
  const [personalDataVisible, setPersonalDataVisible] = useState(false);

  const maskValue = (value) => {
    if (!value || value === "-") {
       return value;
    }
    return personalDataVisible ? value : "••••••••";
  };

  const handleEditClick = () => {
    setEditing(true);
  };

  const handleSelectChange = (value) => {
    console.log(value);
    setSelectedStatus(value === "approved" ? "pass" : "fail");
  };

  const handleStatusChangeConfirmation = async () => {
    try {
      await axios.post(`user/flow/${flowId}/manual_review`, {
        status: selectedStatus,
        flow_id: flowId,
      });
      setEditing(false);
      setManualType(true);
    } catch (error) {
      console.error("Error trying to change status:", error);
      setSelectedStatus(transactionInfo?.flow_status);
    }
  };

  const handleStatusChangeDecline = () => {
    setEditing(false);
    setSelectedStatus(transactionInfo?.flow_status);
  };

  const extractFlowFormIdentityFromStatuses = () => extractFlowFormIdentity(transactionInfo);

  const getFlowPipeline = async () => {
    if (flowId in flowPipelineCache) {
      const cachedData = flowPipelineCache[flowId];
      if (cachedData?.input_data) {
        setInputData(cachedData.input_data);
      }
      return cachedData?.pipeline || null;
    }

    try {
      const response = await axios.get(`/user/flow/${flowId}`);
      const pipeline = response?.data.template?.pipeline || null;
      const input = response?.data.input_data || {};
      const identityForm = extractFlowFormIdentityFromStatuses();
      const inputDataObj = {
        iin: input.iin || identityForm?.iin || "-",
        phone_number: input.phone_number || identityForm?.["phone-number"] || "-"
      };

      setInputData(inputDataObj);

      setFlowPipelineCache((prevCache) => ({
        ...prevCache,
        [flowId]: {
          pipeline: pipeline,
          input_data: inputDataObj,
        },
      }));

      return pipeline;
    } catch (error) {
      console.error("FlowPipeline is not found");
      const identityForm = extractFlowFormIdentityFromStatuses();
      const fallbackData = identityForm
        ? { iin: identityForm.iin || "-", phone_number: identityForm["phone-number"] || "-" }
        : defaultInputData;
      setInputData(fallbackData);
      setFlowPipelineCache((prevCache) => ({
        ...prevCache,
        [flowId]: {
          pipeline: null,
          input_data: fallbackData,
        },
      }));
      return null;
    }
  };

  useEffect(() => {
    if (!visible) {
      setPersonalDataVisible(false);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const attempts = buildServiceAttempts(transactionInfo);
    setServiceAttempts(attempts);
    // The main view shows the last attempt of each service by default
    const defaults = Object.fromEntries(
      Object.entries(attempts).map(([key, list]) => [key, list.length - 1])
    );
    selectedAttemptsRef.current = defaults;
    setSelectedAttempts(defaults);
  }, [transactionInfo, visible]);

  useEffect(() => {
    // Marks this effect run stale so in-flight requests from a previously
    // opened modal don't populate the newly opened one's media.
    let cancelled = false;
    const getTransactionInfo = async () => {
      if (!visible) return;
      setSelectedStatus(transactionInfo?.flow_status);
      setEditedDocData(extractDocumentData());
      const pipelineData = await getFlowPipeline();
      if (cancelled) return;
      setMappedServices({});
      setMappedServices(applySelectedAttempts(extractServiceStatuses(pipelineData)));
      const flow_id = transactionInfo?.flow_id;
      setTransactionForHistory(transactionInfo);

      setDocumentBody((prevDocumentBody) => ({
        ...prevDocumentBody,
        updated_body: {
          ...prevDocumentBody.updated_body,
          created_by: "user",
          editor: {
            id: transactionInfo?.person_id,
            name: transactionInfo?.person_name,
            role: "admin",
          },
          document: {},
        },
      }));

        const dataRequests = [
          {
            label: "signed_docs",
            setters: [setSignedDocs]
          },
          {
            label: "doc_original_front_img",
            tags: ["thumbnail", null],
            setters: [setFrontImg, setLargeFrontImg],
          },
          {
            label: "doc_original_img",
            tags: ["thumbnail", null],
            setters: [setFrontImg, setLargeFrontImg],
          },
          {
            label: "doc_original_back_img",
            tags: ["thumbnail", null],
            setters: [setBackImg, setLargeBackImg],
          },
          { label: "liveness_alt_video", setters: [setLiveVideo] },
          { label: "liveness_video", setters: [setLivenessVideo] },
          { label: "liveness_card", setters: [setLivenessCard] },
          { label: "liveness_frames", setters: [setLivenessFrames] },
          { label: "client_card", setters: [setClientCard] },
          {
            label: "bestframe",
            tags: ["thumbnail", null],
            setters: [setBestframeSmall, setBestframeLarge],
          },
          { label: "egov_doc_pdf", setters: [setEgovDocPdf] },
        ];

      const setLargeImgWithFallback = async (fetchedData) => {
        if (cancelled) return;
        const fallbackLabels = [
          "bestframe",
          "doc_original_front_img",
          "doc_original_img",
          "doc_original_back_img",
        ];

        for (const label of fallbackLabels) {
          if (fetchedData[label]) {
            setLargeImg(fetchedData[label]["default"]);
            return;
          }
        }

        for (const label of fallbackLabels) {
          try {
            const response = await axios.get(`/user/resources/file`, {
              params: { flow_id, label },
            });
            if (cancelled) return;
            if (response.data) {
              setLargeImg(response.data);
              return;
            }
          } catch (error) {
            if (error.response?.status !== 404) {
              break;
            }
          }
        }
        setLargeImg(null);
      };


      const fetchData = async () => {
        const fetchedData = {};
        let largeImageSet = false;

        try {
          await Promise.all(
            dataRequests.map(async ({ label, tags, setters }) => {
              const tagArray = tags || [null];
              await Promise.all(
                tagArray.map((tag, index) =>
                  axios
                    .get(`/user/resources/file`, {
                      params: { flow_id: flowId, label, tag },
                    })
                    .then((response) => {
                      fetchedData[label] = {
                        ...(fetchedData[label] || {}),
                        [tag || "default"]: response.data || null,
                      };

                      if (cancelled) return;
                      setters[index](response.data || null);
                    })
                    .catch(async (error) => {
                      if (
                        error.response?.status === 404 &&
                        label === "doc_original_front_img"
                      ) {
                        try {
                          const fallbackResponse = await axios.get(
                            `/user/resources/file`,
                            {
                              params: {
                                flow_id: flowId,
                                label: "doc_original_img",
                                tag,
                              },
                            }
                          );
                          fetchedData["doc_original_img"] = {
                            ...(fetchedData["doc_original_img"] || {}),
                            [tag || "default"]: fallbackResponse.data || null,
                          };
                          if (!cancelled) setters[index](fallbackResponse.data || null);
                        } catch {
                          if (!cancelled) setters[index](null);
                        }
                      } else {
                        if (!cancelled) setters[index](null);
                      }
                    })
                )
              );
            })
          );
        } finally {
          if(Object.keys(fetchedData).length == 0){
            const labels = dataRequests.map((request) => request.label);
            labels.forEach((label) => {
              fetchedData[label] = {
                ...(fetchedData[label] || {}),
                thumbnail: null,
                default: null,
              };
            });
          }
          updateCache(flowId, fetchedData);
        }
      };

      if (visible && flowId) {
        if (cacheState[flowId]) {
          const cachedData = cacheState[flowId];
          dataRequests.forEach(({ label, tags, setters }) => {
            const tagArray = tags || [null];
            tagArray.forEach((tag, index) => {
              const cachedImage = cachedData[label]?.[tag || "default"];
              if (cachedImage && !cancelled) {
                setters[index](cachedImage);
              }
            });
          });

          if (!largeImg) {
            await setLargeImgWithFallback(cachedData);
          }
          if (!cancelled) setShowSpinner(false);
        } else {
          await fetchData();
          if (!cancelled) setShowSpinner(false);
        }
      }

      // const timer = setTimeout(() => {
      //   setShowSpinner(false);
      //   setShowNoImage(true);
      // }, 5000);

      // return () => {
      //   clearTimeout(timer);
      // };
      }

      getTransactionInfo();

      return () => {
        cancelled = true;
      };
  }, [transactionInfo, visible, flowId, cacheState]);

  // Media labels re-fetched when the user switches an attempt of a service.
  // Requests are made with session_id only (no flow_id): vl2 drops session_id
  // from archive-building paths when flow_id is present.
  const attemptMediaRequests = {
    "Liveness Detection": [
      { label: "liveness_alt_video", setters: [setLiveVideo] },
      { label: "liveness_video", setters: [setLivenessVideo] },
      { label: "liveness_card", setters: [setLivenessCard] },
      { label: "liveness_frames", setters: [setLivenessFrames] },
      {
        label: "bestframe",
        tags: ["thumbnail", null],
        setters: [setBestframeSmall, setBestframeLarge],
      },
    ],
    "Document Recognition": [
      {
        label: "doc_original_front_img",
        tags: ["thumbnail", null],
        setters: [setFrontImg, setLargeFrontImg],
        fallbackLabel: "doc_original_img",
      },
      {
        label: "doc_original_back_img",
        tags: ["thumbnail", null],
        setters: [setBackImg, setLargeBackImg],
      },
      { label: "egov_doc_pdf", setters: [setEgovDocPdf] },
    ],
    "Esign - sign": [{ label: "signed_docs", setters: [setSignedDocs] }],
  };

  const fetchAttemptFile = async (sessionId, label, tag, fallbackLabel) => {
    try {
      const response = await axios.get(`/user/resources/file`, {
        params: { session_id: sessionId, label, tag },
      });
      return response.data || null;
    } catch (error) {
      if (error.response?.status === 404 && fallbackLabel) {
        try {
          const fallbackResponse = await axios.get(`/user/resources/file`, {
            params: { session_id: sessionId, label: fallbackLabel, tag },
          });
          return fallbackResponse.data || null;
        } catch {
          return null;
        }
      }
      return null;
    }
  };

  const loadAttemptArtifacts = async (serviceKey, sessionId) => {
    const requests = attemptMediaRequests[serviceKey];
    if (!requests) return;

    const seq = (attemptFetchSeq.current[serviceKey] || 0) + 1;
    attemptFetchSeq.current[serviceKey] = seq;
    const isStale = () => attemptFetchSeq.current[serviceKey] !== seq;

    setAttemptLoading((prev) => ({ ...prev, [serviceKey]: true }));

    const cachedSession = attemptArtifactCache[sessionId] || {};
    const fetchedData = {};

    await Promise.all(
      requests.map(async ({ label, tags, setters, fallbackLabel }) => {
        const tagArray = tags || [null];
        await Promise.all(
          tagArray.map(async (tag, index) => {
            const tagKey = tag || "default";
            let data;
            if (cachedSession[label] && tagKey in cachedSession[label]) {
              data = cachedSession[label][tagKey];
            } else {
              data = await fetchAttemptFile(sessionId, label, tag, fallbackLabel);
            }
            fetchedData[label] = {
              ...(fetchedData[label] || {}),
              [tagKey]: data,
            };
            if (!isStale()) {
              setters[index](data);
            }
          })
        );
      })
    );

    setAttemptArtifactCache((prev) => ({
      ...prev,
      [sessionId]: { ...(prev[sessionId] || {}), ...fetchedData },
    }));

    if (!isStale()) {
      if (serviceKey === "Liveness Detection") {
        setShowLiveVideo(false);
        setLargeImg(fetchedData.bestframe?.default ?? null);
      } else if (serviceKey === "Document Recognition") {
        // Reset the main preview to the front side of the newly selected attempt
        setLargeImg(
          fetchedData.doc_original_front_img?.default ??
            fetchedData.doc_original_img?.default ??
            fetchedData.doc_original_back_img?.default ??
            null
        );
      }
      setAttemptLoading((prev) => ({ ...prev, [serviceKey]: false }));
    }
  };

  const buildRowForProjection = (row, projection) => {
    const statusShim = {
      service_name: projection.service_name,
      session_status: projection.session_status,
      session_result: projection.result,
    };

    return {
      ...row,
      status: projection.session_status || "Unknown",
      color: determineColor(statusShim),
      result: projection.result
        ? projection.service_name === "veriface"
          ? projection.result.score + "%"
          : capitalizeFirstLetter(
              (projection.result.service_status || "unknown").replace(/_/g, " ")
            )
        : "Unknown",
      error: getErrorInfo(statusShim),
      ...(projection.vendor ? { vendor: projection.vendor } : {}),
    };
  };

  // Rows rebuilt from `service_statuses` always describe the last attempt of a
  // service, so re-apply whatever attempt is currently selected. Without this a
  // rebuild (this effect re-runs whenever the media cache lands) silently
  // reverts the row to the last attempt while the switcher still points at the
  // selected one.
  const applySelectedAttempts = (rows) => {
    const attempts = buildServiceAttempts(transactionInfo);
    const next = { ...rows };

    Object.entries(selectedAttemptsRef.current).forEach(([serviceKey, index]) => {
      const list = attempts[serviceKey];
      if (!list || index === list.length - 1) return;
      const projection = list[index];
      if (!projection || !next[serviceKey]) return;
      next[serviceKey] = buildRowForProjection(next[serviceKey], projection);
    });

    return next;
  };

  const handleAttemptChange = async (serviceKey, newIndex) => {
    const attempts = serviceAttempts[serviceKey];
    const projection = attempts?.[newIndex];
    if (!projection) return;

    selectedAttemptsRef.current = {
      ...selectedAttemptsRef.current,
      [serviceKey]: newIndex,
    };
    setSelectedAttempts((prev) => ({ ...prev, [serviceKey]: newIndex }));

    setMappedServices((prev) => {
      const row = prev[serviceKey];
      if (!row) return prev;
      return { ...prev, [serviceKey]: buildRowForProjection(row, projection) };
    });

    await loadAttemptArtifacts(serviceKey, projection.session_id);
  };

  const handleEditDocClick = () => {
    setIsDocEditing(true);
  };

  const handleDoneDocClick = async () => {
    setIsDocEditing(false);
    setDocumentBody((prevDocumentBody) => {
      const updatedBody = { ...prevDocumentBody };
      Object.entries(editedDocData).forEach(([key, value]) => {
        updatedBody.updated_body.document[value[1]] = value[0];
      });
      return updatedBody;
    });
    try {
      const response = await axios.put(
        `/user/journal/session/${transactionInfo?.session_id}/result`,
        documentBody
      );
      return response.data;
    } catch (error) {
      return error.message;
    }
  };

  const handleCancelDocClick = () => {
    setIsDocEditing(false);
    setEditedDocData(extractDocumentData());
  };

  const handleInputDocChange = (key, value) => {
    setEditedDocData((prevData) => ({
      ...prevData,
      [key]: [value, prevData[key][1]],
    }));
  };

  const handleClearAfterClose = () => {
    setIsDocEditing(false);
    setEditing(false);
    setFaceImg(null);
    setFrontImg(null);
    setBackImg(null);
    setLargeImg(null);
    setLiveVideo(null);
    setLivenessVideo(null);
    setLivenessCard(null);
    setLivenessFrames(null);
    setClientCard(null);
    setEgovDocPdf(null);
    setShowSpinner(true);
    setShowNoImage(false);
    setShowLiveVideo(false);
    setRotateDegree(0);
    setBestframeLarge(null);
    setBestframeSmall(null);
    setLargeBackImg(null);
    setLargeFrontImg(null);
    setSignedDocs(null);
    setMappedServices({});
    setCurrentPage("main");
    setEditedDocData(extractDocumentData());
    setInputData(defaultInputData);
    setServiceAttempts({});
    setSelectedAttempts({});
    selectedAttemptsRef.current = {};
    setAttemptArtifactCache({});
    setAttemptLoading({});
    attemptFetchSeq.current = {};
  };

  const artifacts = { largeImg, liveVideo, livenessVideo, livenessCard, livenessFrames, clientCard, egovDocPdf, signedDocs };
  const { download, mediaButtons } = createDownloadHandlers(flowId, artifacts);
  const serviceArtifactMap = buildServiceArtifactMap(artifacts, download);

  const handleLiveOnScreen = () => {
    setShowLiveVideo(!showLiveVideo);
  };

  const handleHistoryOpen = () => {
    setHistoryOpen(true);
  };
  const handleHistoryClose = () => {
    setHistoryOpen(false);
  };
  const handleChangeLargeImage = (side) => {
    setShowLiveVideo(false);
    if (side === "front") {
      setLargeImg(largeFrontImg);
    } else if (side === "back") {
      setLargeImg(largeBackImg);
    } else {
      setLargeImg(bestframeLarge);
    }
  };

  const getActionButton = (currentService) => {
    const availableArtifacts =
      currentService === "Esign - sign"
        ? (serviceArtifactMap["Esign"] ?? [])
        : (serviceArtifactMap[currentService] ?? []);

    if (availableArtifacts.length === 0) return "-";

    if (availableArtifacts.length === 1) {
      const artifact = availableArtifacts[0];
      return (
        <Tooltip title={artifact.label}>
          <Button size="small" icon={<DownloadOutlined />} onClick={artifact.onClick}>
            Download
          </Button>
        </Tooltip>
      );
    }

    const menu = (
      <Menu>
        {availableArtifacts.map(({ key, label, onClick }) => (
          <Menu.Item key={key} onClick={onClick}>
            {label}
          </Menu.Item>
        ))}
      </Menu>
    );

    return (
      <Dropdown overlay={menu} trigger={["click"]}>
        <Button size="small">
          <DownloadOutlined /> Download <DownOutlined />
        </Button>
      </Dropdown>
    );
  }

  const renderPage = () => {
    switch (currentPage) {
      case "main":
        return (
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              display: "flex",
              gap: "20px",
              justifyContent: "center",
              margin: "0 auto 0 auto",
              padding: "16px 32px 16px 32px",
            }}
          >
            <div className="card-media" style={{ minWidth: "425px" }}>
              <div>
                <Typography.Title level={3} className="card-title">
                  Flow ID
                </Typography.Title>
                <Typography.Text
                  copyable
                  className="card-subtitle"
                  id="flow-main"
                >
                  {transactionInfo?.flow_id}
                </Typography.Text>
              </div>
              <div
                className="table-card image-large"
                style={{ height: "275px" }}
              >
                <div className="card-image-large-upper">
                  {showSpinner && !showNoImage ? (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                      }}
                    >
                      <Spin spinning />
                    </div>
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                      }}
                    >
                      {largeImg && !showLiveVideo ? (
                        <Image
                          src={largeImg}
                          alt="Large face"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            width: "auto",
                            height: "auto",
                            rotate: `${rotateDegree}deg`,
                          }}
                          width={"100%"}
                          height={"100%"}
                          preview={{
                            visible: visibleLarge,
                            src: largeImg,
                            onVisibleChange: (value) => {
                              setVisibleLarge(value);
                            },
                          }}
                        />
                      ) : showLiveVideo ? (
                        <video
                          controls="true"
                          autoplay
                          alt="liveness video"
                          style={{
                            width: "100%",
                            height: "100%",
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                          playsInline
                        >
                          <source src={liveVideo} type="video/mp4" />
                        </video>
                      ) : largeImg == null || showNoImage ? (
                        <div>No Content</div>
                      ) : null}
                    </div>
                  )}
                </div>
                <div className="card-image-large-lower">
                  <>
                    <DownloadOutlined
                      className="card-image-large-control-btn"
                      onClick={
                        showLiveVideo
                          ? download.livenessVideo
                          : download.largeImg
                      }
                    />
                    <UndoOutlined
                      className="card-image-large-control-btn"
                      onClick={() => setRotateDegree(rotateDegree - 90)}
                    />
                    <FullscreenOutlined
                      className="card-image-large-control-btn"
                      onClick={() => setVisibleLarge(true)}
                    />
                    <RedoOutlined
                      className="card-image-large-control-btn"
                      onClick={() => setRotateDegree(rotateDegree + 90)}
                    />
                  </>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  columnGap: "16px",
                }}
              >
                <div
                  className={`card-image-small ${frontImg ? "clickable" : ""}`}
                  onClick={
                    frontImg ? () => handleChangeLargeImage("front") : undefined
                  }
                >
                  <div className="card-image-small-upper">
                    {frontImg ? (
                      <img
                        src={frontImg}
                        alt="doc front"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          objectPosition: "top",
                          borderRadius: "10px 10px 0px 0px",
                        }}
                      />
                    ) : showSpinner ? (
                      <Skeleton.Image
                        active
                        style={{ borderRadius: "10px 10px 0px 0px" }}
                      />
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          minHeight: "73px",
                          width: "130px",
                          backgroundColor: "#F5F5F5",
                          objectFit: "cover",
                          borderRadius: "10px 10px 0px 0px",
                          justifyContent: "center",
                        }}
                      >
                        No Content
                      </div>
                    )}
                  </div>
                  <div className="card-image-small-lower">
                    <p className="modal-card-text">Front</p>
                  </div>
                </div>
                <div
                  className={`card-image-small ${backImg ? "clickable" : ""}`}
                  onClick={
                    backImg ? () => handleChangeLargeImage("back") : undefined
                  }
                >
                  <div className="card-image-small-upper">
                    {backImg ? (
                      <img
                        src={backImg}
                        alt="doc back"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          objectPosition: "top",
                          borderRadius: "10px 10px 0px 0px",
                        }}
                      />
                    ) : showSpinner ? (
                      <Skeleton.Image
                        active
                        style={{ borderRadius: "10px 10px 0px 0px" }}
                      />
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          minHeight: "73px",
                          width: "130px",
                          backgroundColor: "#F5F5F5",
                          objectFit: "cover",
                          borderRadius: "10px 10px 0px 0px",
                          justifyContent: "center",
                        }}
                      >
                        No Content
                      </div>
                    )}
                  </div>
                  <div className="card-image-small-lower">
                    <p className="modal-card-text">Back</p>
                  </div>
                </div>
                <div
                  className={`card-image-small ${
                    bestframeSmall ? "clickable" : ""
                  }`}
                  onClick={
                    bestframeSmall
                      ? () => handleChangeLargeImage("face")
                      : undefined
                  }
                >
                  <div className="card-image-small-upper">
                    {bestframeSmall ? (
                      <img
                        src={bestframeSmall}
                        alt="doc face"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : showSpinner ? (
                      <Skeleton.Image
                        active
                        style={{ borderRadius: "10px 10px 0px 0px" }}
                      />
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          minHeight: "73px",
                          width: "130px",
                          backgroundColor: "#F5F5F5",
                          objectFit: "cover",
                          borderRadius: "10px 10px 0px 0px",
                          justifyContent: "center",
                        }}
                      >
                        No Content
                      </div>
                    )}
                  </div>
                  <div className="card-image-small-lower">
                    <p className="modal-card-text">Face</p>
                  </div>
                </div>
              </div>
              <div
                className="card-media-video"
                style={{ display: liveVideo ? "flex" : "none" }}
              >
                <div
                  className="card-image-small clickable"
                  onClick={handleLiveOnScreen}
                >
                  <div className="card-image-small-upper">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "130px",
                        height: "108px",
                        backgroundColor: "#F5F5F6",
                        borderRadius: "10px 10px 0px 0px",
                        backdropFilter: "blur(50px)",
                      }}
                    >
                      {showLiveVideo ? (
                        <LoadingOutlined style={{ fontSize: "28px" }} />
                      ) : (
                        <PlayCircleOutlined className="card-video-play-btn" />
                      )}
                    </div>
                  </div>
                  <div className="card-image-small-lower">
                    <p className="modal-card-text">Video</p>
                  </div>
                </div>
              </div>
              {mediaButtons.length > 0 && (
                <div className="card-media-btns">
                  {mediaButtons.map(({ stateKey, label }) => (
                    <Button key={stateKey} onClick={download[stateKey]}>{label}</Button>
                  ))}
                </div>
              )}
            </div>
            <div
              style={{ minWidth: "600px", width: "100%" }}
              className="card-media"
            >
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  justifyContent: "space-between",
                }}
              >
                <div className="card-data">
                  <div>
                    <Typography.Title level={3} className="card-title">
                      Status
                    </Typography.Title>
                    {editing ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      > 
                        <Select
                          defaultValue={
                            selectedStatus === "pass" ? "Approved" : "Denied"
                          }
                          onChange={handleSelectChange}
                          style={{ width: 120 }}
                          id="select-status"
                        >
                          {statusOptions.map((option) => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            columnGap: "12px",
                            marginLeft: "30px",
                          }}
                        >
                          <Button
                            icon={
                              <CheckOutlined style={{ fontSize: "12px" }} />
                            }
                            onClick={handleStatusChangeConfirmation}
                            style={{ width: "20px", height: "20px" }}
                            type="success"
                            id="save-status-btn"
                          />

                          <Button
                            icon={
                              <CloseOutlined style={{ fontSize: "12px" }} />
                            }
                            onClick={handleStatusChangeDecline}
                            style={{ width: "20px", height: "20px" }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "start",
                          columnGap: "28px",
                          alignItems: "start",
                        }}
                      >
                        <Typography.Text className="card-subtitle">
                          {selectedStatus === "pass" ? "Approved" : "Denied"}
                        </Typography.Text>
                        {canManualEdit ? 
                        <FormOutlined
                          onClick={handleEditClick}
                          id="edit-status-btn"
                          style={{
                            cursor: "pointer",
                            fontSize: "16px",
                            color: "#507FFB",
                          }}
                        /> : ""}
                      </div>
                    )}
                  </div>

                  <div className="table-card" style={{ height: "100%" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "start",
                      }}
                    >
                      <Typography.Title level={4} className="data-title">
                        Transaction Data
                      </Typography.Title>
                      <Button
                        type="text"
                        size="small"
                        id="toggle-personal-data-btn"
                        icon={personalDataVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                        onClick={() => setPersonalDataVisible(!personalDataVisible)}
                      />
                    </div>

                    <div className="table-status-data">
                      <div className="data-item">
                        <Typography.Text className="data-item-title">
                          Date:
                        </Typography.Text>
                        <Typography.Text className="data-item-value">
                          {convertDateFormat(transactionInfo?.created_at)}
                        </Typography.Text>
                      </div>
                      <div className="data-item">
                        <Typography.Text className="data-item-title">
                          Flow ID:
                        </Typography.Text>
                        <Typography.Text
                          ellipsis
                          className="data-item-value"
                          id="flow-transaction-data"
                        >
                          {flowId}
                        </Typography.Text>
                      </div>
                      <div className="data-item">
                        <Typography.Text className="data-item-title">
                          Person ID:
                        </Typography.Text>

                        <Typography.Text className="data-item-value" ellipsis>
                          {transactionInfo?.person_id}
                        </Typography.Text>
                      </div>
                      <div className="data-item">
                        <Typography.Text className="data-item-title">
                          Country:
                        </Typography.Text>
                        <Typography.Text className="data-item-value">
                          {convertToCountryName(transactionInfo?.country_code)}
                        </Typography.Text>
                      </div>
                      {/* <div className="data-item">
                        <Typography.Text className="data-item-title">
                          Device:
                        </Typography.Text>
                        <Typography.Text className="data-item-value">
                          {transactionInfo?.device}
                        </Typography.Text>
                      </div> */}
                      <div className="data-item">
                        <Typography.Text className="data-item-title">
                          Start Time:
                        </Typography.Text>
                        <Typography.Text className="data-item-value">
                          {convertDateFormat(transactionInfo?.created_at)}
                        </Typography.Text>
                      </div>
                      <div className="data-item">
                        <Typography.Text className="data-item-title">
                          End Time:
                        </Typography.Text>
                        <Typography.Text className="data-item-value">
                          {convertDateFormat(transactionInfo?.updated_at)}
                        </Typography.Text>
                      </div>
                      <div className="data-item">
                        <Typography.Text className="data-item-title">
                          Duration:
                        </Typography.Text>
                        <Typography.Text className="data-item-value">
                          {calculateDurationInSeconds(
                            transactionInfo?.created_at,
                            transactionInfo?.updated_at
                          )}
                          s
                        </Typography.Text>
                      </div>
                      <div className="data-item">
                        <Typography.Text className="data-item-title">
                          IIN:
                        </Typography.Text>
                        <Typography.Text className="data-item-value">
                          {maskValue(inputData.iin)}
                        </Typography.Text>
                      </div>
                      <div className="data-item">
                        <Typography.Text className="data-item-title">
                          Phone
                        </Typography.Text>
                        <Typography.Text className="data-item-value">
                          {maskValue(inputData.phone_number)}
                        </Typography.Text>
                      </div>
                      <div className="data-item">
                        <Typography.Text className="data-item-title">
                          IP Address{transactionInfo?.user_ips?.length > 1 ? 'es' : ''}:
                        </Typography.Text>
                        <Typography.Text className="data-item-value">
                          {transactionInfo?.user_ips?.length > 0
                            ? transactionInfo.user_ips.join(', ')
                            : '-'}
                        </Typography.Text>
                      </div>
                      {/* <div c
                      {/* <div className="data-item">
                        <Typography.Text className="data-item-title">
                          IP:
                        </Typography.Text>
                        <Typography.Text className="data-item-value">
                          {transactionInfo?.ip}
                        </Typography.Text>
                      </div> */}
                    </div>
                  </div>
                </div>
                <div className="card-data">
                  <div>
                    <Typography.Title level={3} className="card-title">
                      Type of Check
                    </Typography.Title>
                    <Typography.Text
                      style={{ textTransform: "capitalize" }}
                      className="card-subtitle"
                    >
                      {transactionInfo?.check_type === "auto"
                        ? "Auto"
                        : "Manual"}
                    </Typography.Text>
                  </div>
                  <div className="table-card" style={{ height: "100%" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "start",
                      }}
                    >
                      <Typography.Title level={4} className="data-title">
                        Document Data
                      </Typography.Title>
                      {isDocEditing ? (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            columnGap: "12px",
                          }}
                        >
                          <Button
                            icon={
                              <CheckOutlined style={{ fontSize: "12px" }} />
                            }
                            onClick={handleDoneDocClick}
                            style={{ width: "20px", height: "20px" }}
                            type="success"
                            id="save-document-data-btn"
                          />

                          <Button
                            icon={
                              <CloseOutlined style={{ fontSize: "12px" }} />
                            }
                            onClick={handleCancelDocClick}
                            style={{ width: "20px", height: "20px" }}
                          />
                        </div>
                      ) : (
                        canManualEdit && Object.keys(editedDocData).length != 0 &&
                        <EditOutlined
                          style={{
                              fontSize: "16px",
                              opacity: personalDataVisible ? 1 : 0.3,
                              cursor: personalDataVisible ? "pointer" : "not-allowed"
                          }}
                          onClick={personalDataVisible ? handleEditDocClick : null}
                          id="edit-document-data-btn"
                        />
                      )}
                    </div>
                    <div className="table-status-data">
                      {Object.keys(editedDocData).length == 0
                        ? Object.keys(defaultDocData).map((key) => (
                            <div key={key} className="data-item">
                              <Typography.Text className="data-item-title">
                                {key}:
                              </Typography.Text>
                              <Typography.Text className="data-item-value">
                                {maskValue(defaultDocData[key])}
                              </Typography.Text>
                            </div>
                          ))
                        : Object.entries(editedDocData).map(([key, value]) => (
                            <div key={key} className="data-item">
                              {isDocEditing ? (
                                <div class="form__group">
                                  <input
                                    type={key}
                                    id={key}
                                    class="form__field"
                                    placeholder={value[0]}
                                    value={value[0]}
                                    onChange={(e) =>
                                      handleInputDocChange(key, e.target.value)
                                    }
                                  />
                                  <label for={key} class="form__label">
                                    {key}
                                  </label>
                                </div>
                              ) : (
                                <>
                                  <Typography.Text className="data-item-title">
                                    {key}:
                                  </Typography.Text>
                                  <Typography.Text
                                    className="data-item-value"
                                    id={key}
                                  >
                                    {key === "Gender" ? value[0] : maskValue(value[0])}
                                  </Typography.Text>
                                </>
                              )}
                            </div>
                          ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="table-card">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "start",
                  }}
                >
                  <Typography.Title level={4} className="data-title">
                    Verifications Passed
                  </Typography.Title>
                </div>
                <div className="table-status-data verify">
                  <div className="verify-inner">
                  <div className="data-item">
                    <Typography.Text className="data-item-title">
                      Feature
                    </Typography.Text>
                    <Typography.Text className="data-item-title" style={{display: "flex", justifyContent: "center"}}>
                      Vendor
                    </Typography.Text>
                    <Typography.Text className="data-item-title result" style={{ width: '130px' }}>
                      Result
                    </Typography.Text>
                    <Typography.Text className="data-item-title">
                      Attempts Count
                    </Typography.Text>
                    <Typography.Text className="data-item-title" style={{ width: "160px", flex: "0 0 160px" }}>
                      Error Reason
                    </Typography.Text>
                    <Typography.Text className="data-item-title">
                      Actions
                    </Typography.Text>
                  </div>
                  <hr
                    style={{
                      backgroundColor: "#B6BFDA",
                      height: "1px",
                      width: "100%",
                      border: "none",
                      marginTop: "-4px",
                      marginBottom: "12px",
                    }}
                  />

                  {Object.keys(mappedServices).length === 0 ? (
                    <div style={{ padding: "20px", textAlign: "center" }}>
                      <Spin size="large" />
                    </div>
                  ) : (
                    Object.entries(mappedServices).map(
                      ([service, { status, color, counter, result, error, vendor }]) => (
                        <div key={service} className="data-item">
                          <Typography.Text className="data-item-value data-item-value-centered" style={{whiteSpace: "normal", maxWidth: "120px", wordBreak: "break-word", fontWeight: 500 }}>
                            {service}:
                          </Typography.Text>
                          <Typography.Text className="data-item-value" style={{whiteSpace: "normal", display: "flex", justifyContent: "center", alignItems: "center"}}>
                            <VendorDisplay vendorCode={vendor} showName/>
                          </Typography.Text>
                          <div
                            style={{
                              display: "flex",
                              columnGap: "12px",
                              alignItems: "center",
                              width: '130px',
                              justifyContent: 'flex-start',
                            }}
                          >
                            <Typography.Text
                              className="data-item-value circled"
                              id={service}
                              style={{ minWidth: '70px', lineHeight: '1' }}
                            >
                              {result}
                            </Typography.Text>
                            <div className={`circle ${color}-circle`}></div>
                          </div>
                          <Typography.Text className="data-item-value data-item-value-centered">
                            {serviceAttempts[service]?.length > 1 ? (
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  columnGap: "4px",
                                }}
                              >
                                <Button
                                  size="small"
                                  type="text"
                                  id={`${service}-prev-attempt`}
                                  icon={<LeftOutlined style={{ fontSize: "10px" }} />}
                                  disabled={
                                    (selectedAttempts[service] ?? 0) <= 0 ||
                                    attemptLoading[service]
                                  }
                                  onClick={() =>
                                    handleAttemptChange(
                                      service,
                                      (selectedAttempts[service] ?? 0) - 1
                                    )
                                  }
                                />
                                {/* The counter comes from data already in hand, so it
                                    updates on click. Only the artifacts below are
                                    fetched, hence the spinner sits beside the counter
                                    rather than replacing it. */}
                                {`${(selectedAttempts[service] ?? 0) + 1}/${
                                  serviceAttempts[service].length
                                }`}
                                {attemptLoading[service] && (
                                  <LoadingOutlined style={{ fontSize: "12px" }} />
                                )}
                                <Button
                                  size="small"
                                  type="text"
                                  id={`${service}-next-attempt`}
                                  icon={<RightOutlined style={{ fontSize: "10px" }} />}
                                  disabled={
                                    (selectedAttempts[service] ?? 0) >=
                                      serviceAttempts[service].length - 1 ||
                                    attemptLoading[service]
                                  }
                                  onClick={() =>
                                    handleAttemptChange(
                                      service,
                                      (selectedAttempts[service] ?? 0) + 1
                                    )
                                  }
                                />
                              </span>
                            ) : (
                              counter
                            )}
                          </Typography.Text>
                          <Tooltip title={renderTooltip(error.tooltip)}>
                            <Typography.Text
                              className="data-item-value"
                              style={{
                                width: "160px",
                                flex: "0 0 160px",
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                ...(error.tooltip && {
                                  textDecoration: "underline dotted",
                                }),
                              }}
                            >
                              {error.msg || "-"}
                            </Typography.Text>
                          </Tooltip>
                          <Typography.Text className="data-item-value data-item-value-centered">
                            {getActionButton(service)}
                          </Typography.Text>
                        </div>
                      )
                    )
                  )}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "row-reverse" }}>
                <Button
                  onClick={() => setCurrentPage("history")}
                  style={{ width: "165px" }}
                  id="history-btn"
                >
                  View history
                </Button>
              </div>
            </div>
          </div>
        );
      case "history":
        return (
          <TransactionHistoryPage
            visible={historyOpen}
            onCancel={handleHistoryClose}
            transactionInfo={transactionForHistory}
            setCurrentPage={setCurrentPage}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={window.innerWidth - SIDEBAR_WIDTH}
      bodyStyle={{
        overflowY: "auto",
        borderRadius: "20px",
      }}
      style={{ left: MODAL_LEFT_OFFSET, top: MODAL_TOP_OFFSET, minWidth: MODAL_MIN_WIDTH, maxWidth: MODAL_MAX_WIDTH }}
      className="transaction-modal"
      afterClose={handleClearAfterClose}
      maskClosable
    >
      {renderPage()}
    </Modal>
  );
};

export default TransactionModalPage;
